import React from 'react';
import { 
  Code2, 
  Rocket, 
  Target, 
  CheckCircle2, 
  Users, 
  Brain,
  Lightbulb,
  GitBranch,
  Shield,
  Clock,
  ArrowRight,
  BarChart
} from 'lucide-react';

const Approach = () => {
  const methodologies = [
    {
      icon: <Brain className="w-8 h-8 text-yellow-400" />,
      title: "Discovery & Strategy",
      description: "We begin with a deep dive into your business needs, objectives, and technical requirements to create a comprehensive roadmap."
    },
    {
      icon: <Code2 className="w-8 h-8 text-yellow-400" />,
      title: "Agile Development",
      description: "Our iterative development process ensures flexibility, continuous feedback, and rapid adaptation to changing needs."
    },
    {
      icon: <Shield className="w-8 h-8 text-yellow-400" />,
      title: "Quality Assurance",
      description: "Rigorous testing and quality control measures are integrated throughout the development lifecycle."
    },
    {
      icon: <Rocket className="w-8 h-8 text-yellow-400" />,
      title: "Deployment & Support",
      description: "Smooth deployment process followed by comprehensive support and maintenance services."
    }
  ];

  const principles = [
    {
      title: "User-Centric Design",
      description: "Every feature and interface is crafted with the end-user in mind, ensuring intuitive and engaging experiences.",
      metrics: ["98% User Satisfaction", "40% Reduced Learning Curve", "60% Higher Engagement"]
    },
    {
      title: "Scalable Architecture",
      description: "Our solutions are built to grow with your business, handling increased loads without compromising performance.",
      metrics: ["99.9% Uptime", "50ms Response Time", "10x Growth Ready"]
    },
    {
      title: "Security First",
      description: "We implement industry-leading security practices to protect your data and maintain compliance standards.",
      metrics: ["Zero Breaches", "Regular Audits", "Compliance Ready"]
    }
  ];

  const processSteps = [
    {
      phase: "Phase 1",
      title: "Discovery & Planning",
      steps: [
        "Requirements gathering",
        "Technical assessment",
        "Architecture planning",
        "Timeline definition"
      ]
    },
    {
      phase: "Phase 2",
      title: "Design & Development",
      steps: [
        "UI/UX design",
        "Core development",
        "Integration setup",
        "Progress reviews"
      ]
    },
    {
      phase: "Phase 3",
      title: "Testing & Optimization",
      steps: [
        "Quality assurance",
        "Performance testing",
        "Security audits",
        "User acceptance"
      ]
    },
    {
      phase: "Phase 4",
      title: "Deployment & Support",
      steps: [
        "Production deployment",
        "Monitoring setup",
        "Team training",
        "Ongoing support"
      ]
    }
  ];

  const metrics = [
    { number: '94%', label: 'Projects Delivered On Time' },
    { number: '99%', label: 'Client Satisfaction Rate' },
    { number: '40%', label: 'Average Cost Reduction' },
    { number: '3x', label: 'Faster Time to Market' }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section id="approach-hero" className="relative py-24 bg-slate-900 overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-600/20 to-purple-600/20" />
          <div className="absolute inset-0" style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%239C92AC' fill-opacity='0.05'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")`,
          }} />
        </div>
        <div className="relative container mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center text-white">
            <h1 className="text-5xl font-bold mb-6">Our Development Approach</h1>
            <p className="text-xl text-gray-300 leading-relaxed">
              A systematic and proven methodology that delivers exceptional results.
              We combine industry best practices with cutting-edge technology to create
              solutions that drive business success.
            </p>
          </div>
        </div>
      </section>

      {/* Methodology Grid */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <GitBranch className="w-12 h-12 text-yellow-400 mx-auto mb-6" />
            <h2 className="text-4xl font-bold mb-6">Our Methodology</h2>
            <p className="text-lg text-gray-600">
              A structured approach to software development that ensures quality,
              efficiency, and successful project delivery.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {methodologies.map((method, index) => (
              <div 
                key={index}
                className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 border border-gray-100"
              >
                <div className="mb-6">{method.icon}</div>
                <h3 className="text-xl font-semibold mb-4">{method.title}</h3>
                <p className="text-gray-600">{method.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Core Principles */}
      <section className="py-24 bg-slate-50">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <Target className="w-12 h-12 text-yellow-400 mx-auto mb-6" />
            <h2 className="text-4xl font-bold mb-6">Core Principles</h2>
            <p className="text-lg text-gray-600">
              Our development philosophy is built on proven principles that ensure
              robust, scalable, and user-friendly solutions.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {principles.map((principle, index) => (
              <div 
                key={index} 
                className="bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-all"
              >
                <h3 className="text-xl font-semibold mb-4">{principle.title}</h3>
                <p className="text-gray-600 mb-6">{principle.description}</p>
                <ul className="space-y-3">
                  {principle.metrics.map((metric, idx) => (
                    <li key={idx} className="flex items-center text-sm text-gray-500">
                      <CheckCircle2 className="w-4 h-4 text-yellow-400 mr-2" />
                      {metric}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Development Process */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <Clock className="w-12 h-12 text-yellow-400 mx-auto mb-6" />
            <h2 className="text-4xl font-bold mb-6">Development Process</h2>
            <p className="text-lg text-gray-600">
              Our streamlined process ensures efficient delivery while maintaining
              the highest standards of quality.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {processSteps.map((step, index) => (
              <div 
                key={index}
                className="relative bg-white rounded-xl p-8 shadow-lg hover:shadow-xl transition-all"
              >
                <div className="absolute -top-4 left-8 bg-yellow-400 text-slate-900 px-4 py-1 rounded-full text-sm font-semibold">
                  {step.phase}
                </div>
                <h3 className="text-xl font-semibold mb-6 mt-2">{step.title}</h3>
                <ul className="space-y-3">
                  {step.steps.map((item, idx) => (
                    <li key={idx} className="flex items-center text-gray-600">
                      <CheckCircle2 className="w-5 h-5 text-yellow-400 mr-2 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Success Metrics */}
      <section className="py-24 bg-slate-900">
        <div className="container mx-auto px-6">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <BarChart className="w-12 h-12 text-yellow-400 mx-auto mb-6" />
            <h2 className="text-4xl font-bold text-white mb-6">Success Metrics</h2>
            <p className="text-lg text-gray-300">
              Our approach consistently delivers outstanding results for our clients.
            </p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {metrics.map((metric, index) => (
              <div key={index} className="text-center">
                <div className="text-4xl font-bold text-yellow-400 mb-2">{metric.number}</div>
                <div className="text-gray-300">{metric.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <Lightbulb className="w-12 h-12 text-yellow-400 mx-auto mb-6" />
            <h2 className="text-4xl font-bold mb-6">Ready to Start Your Project?</h2>
            <p className="text-xl text-gray-600 mb-8">
              Let's discuss how our proven approach can help bring your ideas to life.
            </p>
            <a
              href="/contact"
              className="inline-flex items-center px-8 py-4 bg-yellow-400 text-slate-900 rounded-lg font-semibold hover:bg-yellow-300 transition-all group"
            >
              Start Your Project
              <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Approach;